import Vue from "vue";

import { Component, Prop, Watch } from "@/decorators";

import "./advancedfilepicker.scss";

type FileType = "image" | "file" | "certificate" | "xls";

const fileTypeExtensions: Record<FileType, string[]> = {
    image: ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"],
    file: [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.oasis.opendocument.text",
        "application/pdf",
        "text/plain",
        "application/rtf",
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/webp",
    ],
    xls: ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
    certificate: ["application/x-pkcs12", ".pfx"],
};

@Component
export default class AdvancedFilePickerComponent extends Vue {
    @Prop(String) id: string;
    @Prop(String) name: string;
    @Prop([File, String]) value: File | string;
    @Prop(Boolean) disabled: boolean;
    @Prop({ type: String, default: "100px" }) size: string;
    @Prop({ type: String, default: "file" }) type: FileType;
    @Prop({ type: String, default: "Selecione um arquivo" }) placeholder: string;
    @Prop(String) initialFile: string;
    @Prop(String) initialFileName: string;

    public currentFileName = "";
    public previewUrl = "";
    public isLoading = false;
    public selectedFile: File | null = null;

    get hasFile(): boolean {
        return !!(this.currentFileName || this.previewUrl);
    }

    get acceptedTypes(): string {
        return fileTypeExtensions[this.type].join(",");
    }

    private mounted() {
        if (this.value && typeof this.value === "string") {
            this.previewUrl = this.value;
            this.currentFileName = this.value.split("/").pop() || "Arquivo";
        }
        if (this.initialFile) {
            this.previewUrl = this.initialFile;
        }
        if (this.initialFileName) {
            this.currentFileName = this.initialFileName;
        }
    }

    private openFileDialog() {
        if (!this.disabled) {
            (this.$refs.fileInput as HTMLInputElement).click();
        }
    }

    private onFileChanged(event: Event) {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files[0]) {
            this.onSaveFile(target.files[0]);
        }
    }

    private onSaveFile(file: File) {
        const fileType = file.type;

        if (!fileTypeExtensions[this.type].includes(fileType)) {
            this.$showError("Atenção!", "Arquivo selecionado não é válido");
            return;
        }

        this.currentFileName = file.name;
        this.selectedFile = file;

        if (this.type === "image" && file.type.startsWith("image/")) {
            this.isLoading = true;

            const reader = new FileReader();

            reader.onload = e => {
                this.previewUrl = e.target?.result as string;
                this.isLoading = false;
                this.$emit("input", this.previewUrl);
                this.$emit("file-selected", file);
            };

            reader.onerror = () => {
                this.isLoading = false;
                this.$showError("Erro", "Não foi possível carregar a imagem");
            };

            reader.readAsDataURL(file);
        } else {
            this.previewUrl = "";
            this.$emit("input", file);
            this.$emit("file-selected", file);
        }
    }

    private getFileIcon(): string {
        if (!this.currentFileName) return "fas fa-file";

        const extension = this.currentFileName.split(".").pop()?.toLowerCase();

        switch (extension) {
            case "pdf":
                return "fas fa-file-pdf";
            case "doc":
            case "docx":
                return "fas fa-file-word";
            case "xls":
            case "xlsx":
                return "fas fa-file-excel";
            case "txt":
                return "fas fa-file-alt";
            case "rtf":
                return "fas fa-file-alt";
            case "pfx":
                return "fas fa-certificate";
            default:
                return "fas fa-file";
        }
    }

    @Watch("value")
    private onValueChanged() {
        if (!this.value) {
            this.currentFileName = "";
            this.previewUrl = "";
            this.selectedFile = null;
            if (this.$refs.fileInput) {
                (this.$refs.fileInput as HTMLInputElement).value = "";
            }
        } else if (typeof this.value === "string" && this.value !== this.previewUrl) {
            this.previewUrl = this.value;
            this.currentFileName = this.value.split("/").pop() || "Arquivo";
        }
    }

    @Watch("initialFile")
    private onInitialFileChanged(newValue: string) {
        if (newValue && typeof newValue === "string") {
            this.previewUrl = newValue;
            this.currentFileName = newValue.split("/").pop() || "Arquivo";
        }
    }
}
